import { FamilyStepOption } from "@domain/product/monitoring/family/family-step-option";
import { MonitoringCategory } from "@domain/product/monitoring/monitoring-product.service";
import {
  CentralStationGroupedPartLabels,
  DisplayGroupedPartLabels,
  KvmGroupedPartLabels,
  PatientMonitorGroupedPartLabels,
  TelemetryGroupedPartLabels,
  UtilityGroupedPartLabels,
} from "@project/monitoring/grouped-parts/monitoring-grouped-parts-source-item.factory";

export function isGroupedOption(option?: FamilyStepOption | null): boolean {
  if (!option) return false;

  const devices = option.devices ?? [];
  return devices.length > 1 || !!option.groupImageCantoID?.trim();
}

export function getMonitoringConfigurationSummaryTitle(category: MonitoringCategory): string {
  if (category === MonitoringCategory.CENTRAL_STATIONS) {
    return $localize`:@@monitoring.floorplan.modal.centralStationTitle:Central stations configuration summary`;
  }

  if (category === MonitoringCategory.TELEMETRY) {
    return $localize`:@@monitoring.floorplan.modal.telemetryTitle:Telemetry configuration summary`;
  }

  if (category === MonitoringCategory.KVMS) {
    return $localize`:@@monitoring.floorplan.modal.kvmTitle:KVM configuration summary`;
  }

  if (category === MonitoringCategory.DISPLAYS) {
    return $localize`:@@monitoring.floorplan.modal.displayTitle:Displays configuration summary`;
  }

  if (category === MonitoringCategory.UTILITY) {
    return $localize`:@@monitoring.floorplan.modal.utilityTitle:Utility configuration summary`;
  }

  return $localize`:@@monitoring.floorplan.modal.patientMonitorTitle:Patient monitors configuration summary`;
}

export function getMonitoringOtherProductsLabel(): string {
  return $localize`:@@monitoring.groupedParts.other:Other material numbers`;
}

export function getPatientMonitorGroupedPartLabels(): PatientMonitorGroupedPartLabels {
  return {
    family: $localize`:@@patientMonitorConfiguration.overview.table.column1.category:Family`,
    sensor: $localize`:@@patientMonitorConfiguration.overview.table.column2.category:Sensor`,
    mountingGroup: $localize`:@@patientMonitorConfiguration.overview.table.column3.category:Mounting Group`,
    mountingArm: $localize`:@@patientMonitorConfiguration.overview.table.column4.category:Mounting Arm`,
    attachment: $localize`:@@patientMonitorConfiguration.overview.table.column5.category:Attachment`,
  };
}

export function getUtilityGroupedPartLabels(): UtilityGroupedPartLabels {
  return {
    device: $localize`:@@utilityConfiguration.overview.table.column1.category:Device`,
  };
}

export function getCentralStationGroupedPartLabels(): CentralStationGroupedPartLabels {
  return {
    base: $localize`:@@centralStationConfiguration.overview.baseCategory:Base`,
    licence: $localize`:@@centralStationConfiguration.overview.licenceCategory:Licence`,
    additional: $localize`:@@centralStationConfiguration.overview.additionalCategory:Additional licence`,
    accessory: $localize`:@@centralStationConfiguration.overview.accessoryCategory:Accessory`,
  };
}

export function getTelemetryGroupedPartLabels(): TelemetryGroupedPartLabels {
  return {
    family: $localize`:@@telemetryConfiguration.overview.table.column1.category:Family`,
    sensor: $localize`:@@telemetryConfiguration.overview.table.column2.category:Sensor`,
    additionalBattery: $localize`:@@telemetryConfiguration.overview.table.column3.category:Additional Batteries`,
  };
}

export function getKvmGroupedPartLabels(): KvmGroupedPartLabels {
  return {
    family: $localize`:@@telemetryConfiguration.overview.table.column1.category:Family`,
    transmitter: $localize`:@@kvmConfiguration.overview.table.column2.category:Transmitter`,
    receiver: $localize`:@@kvmConfiguration.overview.table.column3.category:Receiver`,
    accessories: $localize`:@@kvmConfiguration.overview.table.column4.category:Accessories`,
  };
}

export function getDisplayGroupedPartLabels(): DisplayGroupedPartLabels {
  return {
    display: $localize`:@@displayConfiguration.overview.table.column1.category:Display`,
    accessory: $localize`:@@displayConfiguration.overview.table.column2.category:Accessory`,
  };
}

export function getMonitoringPrimaryLabel(category: MonitoringCategory): string {
  switch (category) {
    case MonitoringCategory.DISPLAYS:
      return getDisplayGroupedPartLabels().display;
    case MonitoringCategory.UTILITY:
      return getUtilityGroupedPartLabels().device;
    case MonitoringCategory.CENTRAL_STATIONS:
      return getCentralStationGroupedPartLabels().base;
    case MonitoringCategory.TELEMETRY:
      return getTelemetryGroupedPartLabels().family;
    case MonitoringCategory.KVMS:
      return getKvmGroupedPartLabels().family;
    default:
      return getPatientMonitorGroupedPartLabels().family;
  }
}
